import { useTranslations } from 'next-intl';
import {
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Popover,
} from 'react-aria-components';
import { BsXLg } from 'react-icons/bs';

import { type ListingsFilterName } from '@/features/listing/types/listing-filters-types';
import Button from '@/ui/button/button';

// import styles from '@/features/listing/components/listings-filters/listing-view-select/listing-view-select.module.scss';
import styles from './listing-filter-new.module.scss';

interface ListingFilterNewProps {
  listingFilterName: ListingsFilterName,
  onFilterDisplayChange: (filterName: ListingsFilterName | null) => void,
  buttonText: string | number | ReactElement,
  dialogTitle: string,
  children: ReactNode,
  isPopoverOpen?: boolean,
  setPopoverOpen?: (isOpen: boolean) => void,
  onClear?: () => void,
  onApplyClick?: () => void,
  activeFiltersCount?: number,
}

export default function ListingFilterNew(props: ListingFilterNewProps) {
  const {
    activeFiltersCount = 0,
    buttonText,
    children,
    dialogTitle,
    isPopoverOpen,
    listingFilterName,
    onApplyClick,
    onClear,
    onFilterDisplayChange,
    setPopoverOpen,
  } = props;

  const translationsFiltersCommon = useTranslations('listing.filters.common');

  const handleOpenViewPopover = () => {
    onFilterDisplayChange(listingFilterName);

    if (setPopoverOpen) {
      setPopoverOpen(true);
    }
  };

  const handleCloseViewPopover = () => {
    onFilterDisplayChange(null);

    if (setPopoverOpen) {
      setPopoverOpen(false);
    }
  };

  const handleApplyClick = () => {
    if (onApplyClick) {
      handleCloseViewPopover();
      onApplyClick();
    }
  };

  return (
    <DialogTrigger>
      <AriaButton
        className={styles.listingFilter_showDropdown}
        onPress={handleOpenViewPopover}
      >
        {activeFiltersCount > 0 && (
          <div className={styles.listingFilter_activeFiltersCount}>
            {activeFiltersCount}
          </div>
        )}

        {buttonText}
      </AriaButton>

      <Popover
        className={styles.listingFilter_popover}
        isOpen={isPopoverOpen}
        onOpenChange={setPopoverOpen}
      >
        <Dialog style={{ outline: 'none' }}>
          <div className={styles.listingFilter_top}>
            <p className={styles.listingFilter_title}>
              {dialogTitle}
            </p>

            <AriaButton
              className={styles.listingFilter_closeButton}
              onPress={handleCloseViewPopover}
              aria-label="close dropdown"
            >
              <BsXLg />
            </AriaButton>
          </div>

          <div className={styles.listingFilter_children}>
            {children}
          </div>

          <div className={styles.listingFilter_bottom}>
            <Button
              onClick={onClear}
              type="button"
              element="button"
              size="md"
              variant="ghost"
            >
              {translationsFiltersCommon('clear')}
            </Button>

            <Button
              onClick={handleApplyClick}
              element="button"
              size="md"
            >
              {translationsFiltersCommon('show-results')}
            </Button>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
